import { useAtomValue } from 'jotai'
import { nanoid } from 'nanoid'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { compile } from '~/apps/ninesegsAnalyzer/routes/paths'
import { brandGroupIdAtom } from '~/atoms'
import { useCreateSurveyFilterRestoringParams } from '~/hooks/useCreateSurveyFilterRestoringParams'
import { useSurveyQueryData } from '~/hooks/useSurveyQuery'
import { assertExists } from '~/utils'

import {
  brand1IdAtom,
  brand2IdAtom,
  commentAtom,
  customerAttributeIdsAtom,
  dataDisplayTypeAtom,
  groupInfosAtom,
  groupNameMapAtom,
  groupsDiffsAtom,
  hiddenColumnKeysAtom,
  hiddenRowIdsAtom,
  individualSortValueAtom,
  matrixSortValueAtom,
  questionIdsAtom,
  questionsDiffsMapAtom,
  resultViewerTypeAtom,
  sequentialSortValueAtom,
} from '../../atoms'
import { pathKey } from '../../utils'

import { type RestoringParams } from './types'

export const useCreateRestoringParams = () => {
  const { t } = useTranslation()
  const createSurveyFilterRestoringParams =
    useCreateSurveyFilterRestoringParams()

  const survey = useSurveyQueryData()

  const brandGroupId = useAtomValue(brandGroupIdAtom)

  const brand1Id = useAtomValue(brand1IdAtom)
  const brand2Id = useAtomValue(brand2IdAtom)
  const dataDisplayType = useAtomValue(dataDisplayTypeAtom)
  const groupInfos = useAtomValue(groupInfosAtom)
  const resultViewerType = useAtomValue(resultViewerTypeAtom)
  const questionIds = useAtomValue(questionIdsAtom)
  const commentRecord = useAtomValue(commentAtom)
  const customerAttributeIds = useAtomValue(customerAttributeIdsAtom)
  const groupNameMap = useAtomValue(groupNameMapAtom)

  const sequentialSortValue = useAtomValue(sequentialSortValueAtom)
  const individualSortValue = useAtomValue(individualSortValueAtom)
  const matrixSortValue = useAtomValue(matrixSortValueAtom)

  const hiddenRowIds = useAtomValue(hiddenRowIdsAtom)
  const hiddenColumnKeys = useAtomValue(hiddenColumnKeysAtom)

  const groupsDiffs = useAtomValue(groupsDiffsAtom)
  const questionsDiffsMap = useAtomValue(questionsDiffsMapAtom)

  return React.useCallback(
    (hash: RestoringParams['hash']) => {
      assertExists(survey, t('assertion_failed'))
      assertExists(brandGroupId, t('assertion_failed'))

      const hasComment = (commentRecord[hash] ?? '') !== ''

      const params = createSurveyFilterRestoringParams<RestoringParams>(
        compile(pathKey, { surveyId: survey.id }),
        {
          version: 2,
          revision: 7,
          brandGroupId,
          brand1Id,
          brand2Id,
          comment: commentRecord[hash] ?? '',
          dataDisplayType,
          groupInfos,
          resultViewerType,
          questionIds,
          customerAttributeIds,
          groupNameMap,
          sequentialSortValue,
          individualSortValue,
          matrixSortValue,
          hiddenColumnKeys,
          hiddenRowIds,
          groupsDiffs,
          questionsDiffsMap,
          hash,
        },
      )

      // Attach a unique commentRef only when this figure has a comment, so the saved
      // figure maps 1:1 to its DocumentImage. No comment → no marker, so comment-less
      // shares keep content-dedup.
      return hasComment ? { ...params, commentRef: nanoid() } : params
    },
    [
      brand1Id,
      brand2Id,
      brandGroupId,
      commentRecord,
      createSurveyFilterRestoringParams,
      customerAttributeIds,
      dataDisplayType,
      groupInfos,
      groupNameMap,
      groupsDiffs,
      hiddenColumnKeys,
      hiddenRowIds,
      individualSortValue,
      matrixSortValue,
      questionIds,
      questionsDiffsMap,
      resultViewerType,
      sequentialSortValue,
      survey,
      t,
    ],
  )
}
